import axios, { CanceledError } from "axios";
import envVars from "../env-configs";
import { fetchSourceIpFromProviders } from "./utils/fetchSourceIp";
import { isLogoutInProgress } from "./session/logoutState";

// --- IP Management Logic ---

// 1. State to hold the IP and the status of our attempts
let cachedIp: string | null = null;
let isFetching = false;

// 2. The dedicated function to fetch the IP
const fetchIpAddress = async () => {
  // If we already have an IP or are currently fetching, stop here.
  if (cachedIp || isFetching) return;

  isFetching = true;

  try {
    cachedIp = await fetchSourceIpFromProviders();
    if (!cachedIp) throw new Error("All IP providers failed");
    isFetching = false;
  } catch (error) {
    console.error("Failed to fetch IP. Retrying in 2 minutes.", error);
    isFetching = false;
    
    // Failure: Schedule a retry in 2 minutes (120,000 milliseconds)
    setTimeout(() => {
      fetchIpAddress();
    }, 2 * 60 * 1000);
  }
};

// --- Axios Setup ---

const axiosInstance = axios.create({
  baseURL: envVars.REACT_APP_SERVER_URL,
});

const selectedEndpoints = [
  "/v1/auth/new-flow/register",
  "/v1/orders/new-flow/create",
  "/v1/orders/new-flow",
  "v1/organizations/new-flow/create",
  "v1/sof/create-sof",
  "v1/sof/update-sof",
  "v1/users/invite",
  "v1/orders/new-flow/approval",
];

axiosInstance.interceptors.request.use(async (config: any) => {
  if (isLogoutInProgress()) {
    return Promise.reject(
      new CanceledError("Requests cancelled because logout is in progress")
    );
  }

  try {
    if (selectedEndpoints.some((link) => config.url.includes(link))) {
      // Skip specific endpoints
      if (
        config.url.includes("/v1/orders/new-flow/add-document") ||
        config.url.includes("/v1/orders/new-flow/multiple-download") ||
        config.url.includes("/v1/orders/new-flow/classify-supporting-documents") || 
        config.url.includes("/v1/orders/new-flow/check-duplicate")
      ) {
        return config;
      }

      // 3. Trigger the fetch if we don't have an IP yet
      if (!cachedIp) {
        // We await this so the FIRST request tries to get it.
        // If this fails, the catch block above schedules the 2-minute retry.
        await fetchIpAddress();
      }

      // 4. Attach IP if available
      // (If the fetch failed, cachedIp will be null, and we proceed without it)
      if (cachedIp) {
        const isApproval = config.url === "v1/orders/new-flow/approval";
        config.data = {
          ...config.data,
          [isApproval ? "sourceIpAddress" : "SourceIpAddress"]: cachedIp,
        };
      }
    }
  } catch (error) {
    console.error("Interceptor error:", error);
  }
  return config;
});

export default axiosInstance;